import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import FileUpload from "@/components/FileUpload";
import BookingDialog from "@/components/BookingDialog";
import { Plus, Trash2, Info, Loader2, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";

export default function OtherFurniturePage() {
  const { t, lang } = useLang();
  const so = t.seo.other;
  const [products, setProducts] = useState([{ name: "", article: "", qty: 1 }]);
  const [fileIds, setFileIds] = useState([]);
  const [open, setOpen] = useState(false);
  const [recognizing, setRecognizing] = useState(false);
  const [started, setStarted] = useState(false); // AI done OR manual chosen
  const [recognition, setRecognition] = useState(null);

  useEffect(() => {
    document.title = lang === "da"
      ? "Montering af IKEA-møbler — tilbud | Belov Multiservice"
      : "IKEA furniture assembly — quote | Belov Multiservice";
  }, [lang]);

  const updP = (i, k, v) => setProducts((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addP = () => setProducts((p) => [...p, { name: "", article: "", qty: 1 }]);
  const rmP = (i) => setProducts((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p));

  const analyze = async () => {
    if (!fileIds.length) { toast.error(t.other.analyzeFailed); return; }
    setRecognizing(true);
    try {
      const res = await api.post("/other/recognize", { file_id: fileIds[0] });
      const r = res.data.recognition;
      setRecognition(r);
      const mapped = (r.items || []).map((it) => ({
        name: it.name || it.category || "",
        article: it.article || "",
        qty: it.quantity || 1,
        category: it.category || "",
        confidence: it.confidence,
      }));
      setProducts(mapped.length ? mapped : [{ name: "", article: "", qty: 1 }]);
      setStarted(true);
    } catch (e) {
      toast.error(t.other.analyzeFailed);
      setStarted(true);
    } finally {
      setRecognizing(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none text-base";

  return (
    <div data-testid="other-furniture-page">
      <section className="bg-[#F4F1EA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 px-3 py-1.5 rounded-full">{t.services.quote}</span>
          <h1 className="mt-4 text-3xl sm:text-5xl font-display font-extrabold tracking-tight text-stone-900">{t.other.heroTitle}</h1>
          <p className="mt-4 text-lg text-stone-600">{t.other.heroSubtitle}</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        {/* STEP 1 — Upload + AI recognition */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold text-stone-900">{t.other.uploadStep}</h2>
          <p className="text-sm text-stone-500 mt-1 mb-4">{t.other.uploadHint}</p>
          <FileUpload onChange={setFileIds} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" testId="other-upload" />
          {!started && (
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button onClick={analyze} disabled={recognizing || !fileIds.length}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#1E3A2F] text-white font-bold hover:bg-[#162D24] disabled:opacity-50"
                data-testid="other-analyze-btn">
                {recognizing ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.other.analyzing}</> : <><Sparkles className="w-4 h-4" /> {t.other.analyze}</>}
              </button>
              <button onClick={() => setStarted(true)} disabled={recognizing}
                className="px-5 py-3.5 rounded-full border border-stone-300 font-semibold text-stone-700 hover:bg-stone-50"
                data-testid="other-manual-btn">
                {t.other.fillManually}
              </button>
            </div>
          )}
        </div>

        {started && (
          <>
            {/* STEP 2 — Recognized / editable product list */}
            <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8" data-testid="other-products">
              <div className="flex items-center gap-2 text-[#1E3A2F]">
                <CheckCircle2 className="w-5 h-5" />
                <h2 className="font-display text-xl font-bold">{recognition ? t.other.recognizedTitle : t.other.productsTitle}</h2>
              </div>
              {recognition && <p className="text-sm text-stone-500 mt-1">{t.other.recognizedHint}</p>}

              {recognition?.low_confidence && (
                <p className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="other-low-confidence">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {t.other.lowConfidence}
                </p>
              )}

              <div className="mt-5 space-y-4">
                {products.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`other-product-${i}`}>
                    <div className="col-span-12 sm:col-span-6">
                      <label className="block text-xs font-medium text-stone-500 mb-1">
                        {t.other.productName}
                        {typeof p.confidence === "number" && (
                          <span className={`ml-2 font-mono ${p.confidence < 0.6 ? "text-amber-600" : "text-stone-400"}`} data-testid={`other-confidence-${i}`}>
                            {Math.round(p.confidence * 100)}%
                          </span>
                        )}
                      </label>
                      <input className={inputCls} value={p.name} onChange={(e) => updP(i, "name", e.target.value)} data-testid={`other-name-${i}`} />
                    </div>
                    <div className="col-span-7 sm:col-span-3">
                      <label className="block text-xs font-medium text-stone-500 mb-1">{t.other.articleNo}</label>
                      <input className={inputCls} value={p.article} onChange={(e) => updP(i, "article", e.target.value)} data-testid={`other-article-${i}`} />
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <label className="block text-xs font-medium text-stone-500 mb-1">{t.other.qty}</label>
                      <input type="number" min={1} className={inputCls} value={p.qty} onChange={(e) => updP(i, "qty", parseInt(e.target.value || "1", 10))} data-testid={`other-qty-${i}`} />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      {products.length > 1 && (
                        <button onClick={() => rmP(i)} className="w-full h-[46px] rounded-xl border border-stone-300 flex items-center justify-center text-stone-500 hover:text-red-600 hover:border-red-300" data-testid={`other-remove-${i}`} aria-label={t.other.remove}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={addP} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A2F] hover:gap-2.5 transition-all" data-testid="other-add-product">
                <Plus className="w-4 h-4" /> {t.other.addProduct}
              </button>
            </div>

            <div className="flex items-start gap-2 text-sm text-stone-600 bg-stone-50 rounded-xl p-4">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-stone-400" /> {t.other.quoteNote}
            </div>

            <button onClick={() => setOpen(true)} className="w-full py-4 rounded-full bg-[#1E3A2F] text-white font-bold text-base hover:bg-[#162D24]" data-testid="other-request-quote-btn">
              {t.common.requestQuote}
            </button>
          </>
        )}
      </section>

      {/* SEO CONTENT */}
      <section className="bg-[#F4F1EA]" data-testid="other-seo">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-stone-900">{so.aboutTitle}</h2>
          <p className="mt-4 text-stone-600">{so.aboutIntro}</p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-2.5">
            {so.items.map((it, i) => (
              <li key={i} className="flex items-center gap-2.5 text-stone-700">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> <span>{it}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-stone-600 leading-relaxed">{so.uploadText}</p>
          <p className="mt-4 text-stone-600">
            {so.paxText}{" "}
            <Link to="/pax-montering" className="text-[#1E3A2F] font-semibold underline underline-offset-2" data-testid="other-seo-pax-link">{so.paxLink}</Link>.
          </p>
        </div>
      </section>

      <BookingDialog
        open={open}
        onOpenChange={setOpen}
        serviceType="other"
        configuration={{ products }}
        fileIds={fileIds}
        recognizedComponents={recognition?.items || []}
        source={recognition ? "upload" : "manual"}
        successText={t.other.successMsg}
      />
    </div>
  );
}
